<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWizardConfigStore } from 'dashboard/store/wizard/preferences';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'dashboard/components-next/dropdown-menu/base/DropdownItem.vue';
import { provideDropdownContext } from 'dashboard/components-next/dropdown-menu/base/provider.js';

const props = defineProps({
  featureKey: {
    type: String,
    required: true,
  },
  allowedProviders: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['change']);
const { isOnNurieMangoCloud } = useAccount();

const PROVIDER_ICONS = {
  openai: 'i-ri-openai-fill',
  anthropic: 'i-ri-anthropic-line',
  mistral: 'i-logos-mistral-icon',
  vertexai: 'i-woot-gemini',
};

const iconForModel = model => {
  return PROVIDER_ICONS[model.provider];
};

const { t } = useI18n();
const wizardConfigStore = useWizardConfigStore();
const isOpen = ref(false);

const allModelsForFeature = computed(() =>
  wizardConfigStore.getModelsForFeature(props.featureKey)
);

const availableModels = computed(() => {
  const models = allModelsForFeature.value;
  if (!props.allowedProviders.length) {
    return models;
  }

  return models.filter(model =>
    props.allowedProviders.includes(model.provider)
  );
});

const recommendedModelId = computed(() =>
  wizardConfigStore.getDefaultModelForFeature(props.featureKey)
);

const selectedModel = computed(() =>
  wizardConfigStore.getSelectedModelForFeature(props.featureKey)
);

const selectedModelSource = computed(() =>
  wizardConfigStore.getSelectedModelSourceForFeature(props.featureKey)
);

const featureConfig = computed(() =>
  wizardConfigStore.getFeatureConfig(props.featureKey)
);

const selectedModelId = ref(null);

watch(
  selectedModel,
  newSelected => {
    if (newSelected) {
      selectedModelId.value = newSelected;
    }
  },
  { immediate: true }
);

const selectedModelDetails = computed(() => {
  if (!selectedModelId.value) return null;
  return (
    allModelsForFeature.value.find(m => m.id === selectedModelId.value) || null
  );
});

const resetModelDetails = computed(() => {
  const installationDefault = featureConfig.value?.installation_default;
  const installationDefaultModel = allModelsForFeature.value.find(
    model => model.id === installationDefault
  );

  if (installationDefaultModel) {
    return installationDefaultModel;
  }

  return (
    allModelsForFeature.value.find(
      model => model.id === featureConfig.value?.feature_default
    ) || null
  );
});

const resetModelLabel = computed(() => {
  const hasInstallationDefault = allModelsForFeature.value.some(
    model => model.id === featureConfig.value?.installation_default
  );

  if (hasInstallationDefault) {
    return t('WIZARD_SETTINGS.MODEL_CONFIG.USE_INSTALLATION_DEFAULT');
  }

  return t('WIZARD_SETTINGS.MODEL_CONFIG.USE_FEATURE_DEFAULT');
});

const getCreditLabel = model => {
  const multiplier = model.credit_multiplier || 1;
  return t('WIZARD_SETTINGS.MODEL_CONFIG.CREDITS_PER_MESSAGE', {
    credits: multiplier,
  });
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

provideDropdownContext({
  isOpen,
  toggle: () => toggleDropdown(),
  closeMenu: closeDropdown,
});

const selectModel = model => {
  selectedModelId.value = model.id;
  emit('change', { feature: props.featureKey, model: model.id });
  closeDropdown();
};

const resetModelSelection = () => {
  selectedModelId.value = resetModelDetails.value?.id || null;
  emit('change', { feature: props.featureKey, model: null });
  closeDropdown();
};
</script>

<template>
  <div v-on-clickaway="closeDropdown" class="relative flex-shrink-0">
    <button
      type="button"
      class="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg border-n-weak dark:bg-n-solid-2 dark:hover:bg-n-solid-3 bg-n-alpha-2 hover:bg-n-alpha-1 min-w-[180px] justify-between"
      @click="toggleDropdown"
    >
      <span v-if="selectedModelDetails" class="text-n-slate-12">
        {{ selectedModelDetails.display_name }}
      </span>
      <span v-else class="text-n-slate-10">
        {{ t('WIZARD_SETTINGS.MODEL_CONFIG.SELECT_MODEL') }}
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 text-n-slate-11 transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>
    <DropdownBody
      v-if="isOpen"
      class="absolute right-0 top-full mt-1 min-w-64 z-50 max-h-96 [&>ul]:max-h-96 [&>ul]:overflow-y-scroll"
    >
      <DropdownItem
        v-if="resetModelDetails"
        :click="resetModelSelection"
        class="rounded-lg dark:hover:bg-n-solid-3 hover:bg-n-alpha-1 border-b border-n-weak mb-1"
        :class="{
          'dark:bg-n-solid-3 bg-n-alpha-1': selectedModelSource !== 'account',
        }"
      >
        <div class="flex flex-col w-full text-left gap-1">
          <div class="text-sm font-medium text-n-slate-12">
            {{ resetModelLabel }}
          </div>
          <span class="text-xs text-n-slate-11">
            {{ resetModelDetails.display_name }}
          </span>
        </div>
      </DropdownItem>
      <DropdownItem
        v-for="model in availableModels"
        :key="model.id"
        :click="() => selectModel(model)"
        class="rounded-lg dark:hover:bg-n-solid-3 hover:bg-n-alpha-1"
        :class="{
          'dark:bg-n-solid-3 bg-n-alpha-1': selectedModelId === model.id,
          'pointer-events-none opacity-60': model.coming_soon,
        }"
      >
        <div class="flex gap-2 w-full">
          <Icon :icon="iconForModel(model)" class="size-4 flex-shrink-0" />
          <div class="flex flex-col w-full text-left gap-1">
            <div
              class="text-sm w-full font-medium leading-none text-n-slate-12 flex items-baseline justify-between"
            >
              {{ model.display_name }}
              <span
                v-if="model.id === recommendedModelId"
                class="text-[10px] uppercase text-n-iris-11 border border-1 border-n-iris-10 leading-none rounded-lg px-1 py-0.5"
              >
                {{ t('GENERAL.PREFERRED') }}
              </span>
            </div>
            <span v-if="model.coming_soon" class="text-xs text-n-slate-11">
              {{ t('WIZARD_SETTINGS.MODEL_CONFIG.COMING_SOON') }}
            </span>
            <span
              v-else-if="isOnNurieMangoCloud"
              class="text-xs text-n-slate-11"
            >
              {{ getCreditLabel(model) }}
            </span>
          </div>
        </div>
      </DropdownItem>
    </DropdownBody>
  </div>
</template>
